import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { PredefinedMessageDialogComponent, PredefinedMessageDialogData } from 'src/app/global/components/dialogs/predefined-message-dialog/predefined-message-dialog.component';
import { ConfirmDialogV2Component } from 'src/app/global/components/dialogs/confirm-dialog-v2/confirm-dialog-v2.component';
import { organisationConfigurationSelector, organisationSelector, languageSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';

export interface PredefinedMessage {
    id: string;
    [lang: string]: string;
}

@Component({
    selector: 'app-other-settings',
    templateUrl: './other-settings.component.html',
    styleUrls: ['./other-settings.component.scss'],
})
export class OtherSettingsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    loading = true;
    processingSubmit = false;
    savingMessages = false;
    orgConfig: any;
    orgTag: string;
    langs: string[];
    selectedLang: string;
    predefinedMessages: PredefinedMessage[] = [];

    requestsConfigForm = new FormGroup({
        maxAttachments: new FormControl('', [Validators.required, Validators.pattern('^[0-9]0?$'), Validators.max(10)]),
    });

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        private messageService: MessageService,
        private store: Store<State>,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        this.store
            .select(languageSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((lang) => {
                this.translate.use(lang);
            });

        this.store
            .select(organisationSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((org) => !!org),
            )
            .subscribe((org) => {
                this.orgTag = org.tag;
            });

        combineLatest([this.store.select(organisationConfigurationSelector), this.store.select(languageSelector)])
            .pipe(
                takeUntil(this.destroy$),
                filter(([config]) => !!config),
            )
            .subscribe(([config, lang]) => {
                this.orgConfig = config;
                this.langs = config.lang?.available || ['fr', 'en'];
                this.selectedLang = lang || this.langs[0] || 'fr';
                this.requestsConfigForm.get('maxAttachments').setValue(String(config.request?.maxAttachments || 0));
                this.predefinedMessages = (config.request?.predefinedMessages || []).map((m) => ({ ...m }));
                this.loading = false;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get maxAttachments() {
        return this.requestsConfigForm.get('maxAttachments');
    }

    validateForm(): boolean {
        return this.requestsConfigForm.valid;
    }

    cancel(): void {
        this.requestsConfigForm.get('maxAttachments').setValue(this.orgConfig.request?.maxAttachments || 0);
        this.messageService.success(this.translate.instant('request_v2.settings_dialog.other_settings.messages.cancelled'));
    }

    submit(): void {
        this.processingSubmit = true;
        const payload: PatchOrganisationConfiguration = {
            group: 'request',
            data: {
                maxAttachments: +this.maxAttachments.value,
                predefinedMessages: this.predefinedMessages,
            },
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.orgTag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.processingSubmit = false;
                    this.messageService.success(this.translate.instant('request_v2.settings_dialog.other_settings.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translate.instant('request_v2.settings_dialog.other_settings.messages.error'));
                    console.log(error);
                },
            );
    }

    // --- Predefined Messages ---

    switchLang(lang: string): void {
        this.selectedLang = lang;
    }

    getMessageText(message: PredefinedMessage): string {
        return message[this.selectedLang] || '';
    }

    openAddMessageDialog(): void {
        const dialogData: PredefinedMessageDialogData = {
            mode: 'add',
            langs: this.langs,
        };

        this.dialog
            .open(PredefinedMessageDialogComponent, {
                autoFocus: false,
                panelClass: 'bciti-dialog',
                data: dialogData,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const newMessage: PredefinedMessage = { id: uuid.v4(), ...result };
                    this.predefinedMessages = [...this.predefinedMessages, newMessage];
                    this.saveMessages(this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.addSuccess'));
                }
            });
    }

    openEditMessageDialog(message: PredefinedMessage): void {
        const messageData: { [lang: string]: string } = {};
        for (const lang of this.langs) {
            messageData[lang] = message[lang] || '';
        }

        const dialogData: PredefinedMessageDialogData = {
            mode: 'edit',
            langs: this.langs,
            message: messageData,
        };

        this.dialog
            .open(PredefinedMessageDialogComponent, {
                autoFocus: false,
                panelClass: 'bciti-dialog',
                data: dialogData,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const idx = this.predefinedMessages.findIndex((m) => m.id === message.id);
                    if (idx !== -1) {
                        this.predefinedMessages[idx] = { ...this.predefinedMessages[idx], ...result };
                        this.predefinedMessages = [...this.predefinedMessages];
                        this.saveMessages(this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.editSuccess'));
                    }
                }
            });
    }

    openDeleteMessageDialog(message: PredefinedMessage): void {
        const confirmDialogRef = this.dialog.open(ConfirmDialogV2Component, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.deleteDialog.title'),
                body: this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.deleteDialog.body'),
                buttons: {
                    cancel: {
                        text: this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.deleteDialog.cancel'),
                        class: 'denial-reason-cancel-btn',
                    },
                    confirm: {
                        text: this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.deleteDialog.confirm'),
                        class: 'denial-reason-delete-btn',
                    },
                },
            },
        });

        confirmDialogRef.componentInstance.onSubmit.pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
            if (confirmed) {
                confirmDialogRef.componentInstance.loading = true;
                this.predefinedMessages = this.predefinedMessages.filter((m) => m.id !== message.id);
                this.saveMessages(this.translate.instant('request_v2.settings_dialog.other_settings.predefinedMessages.deleteSuccess'), confirmDialogRef);
            } else {
                confirmDialogRef.close();
            }
        });
    }

    private saveMessages(successMessage: string, dialogRef?: any): void {
        this.savingMessages = true;
        const payload: PatchOrganisationConfiguration = {
            group: 'request',
            data: {
                maxAttachments: this.orgConfig.request?.maxAttachments || 0,
                predefinedMessages: this.predefinedMessages,
            },
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.orgTag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.savingMessages = false;
                    if (dialogRef) dialogRef.close();
                    this.messageService.success(successMessage);
                },
                (error) => {
                    this.savingMessages = false;
                    if (dialogRef) dialogRef.close();
                    this.messageService.error(this.translate.instant('request_v2.settings_dialog.other_settings.messages.error'));
                    console.log(error);
                },
            );
    }
}
